<template>
  <WfmToolbar
    :data-test-id="'form_' + folder.name"
    custom-class="toolbar-class"
  >
    <template #left-utility>
      <div class="d-flex me-2">
        <div>
          <v-icon
            fill="white"
            :name="listIcon"
            scale="1.5"
            style="cursor: pointer;"
            :data-test-id="`${folder.name}/${currentTask}/expandForm`"
            @click="expandTheList"
          />
          <v-tooltip
            activator="parent"
            location="left"
          >
            {{ listExpanded ? 'hide list' : 'show list' }}
          </v-tooltip>
        </div>
        <div>
          <v-icon
            :fill="mandatoryOnlyIconColor"
            class="mt-1"
            name="bi-patch-exclamation"
            scale="1.2"
            style="cursor: pointer;"
            :data-test-id="`${folder.name}/${currentTask}/mandatoryFilter`"
            @click="showOnlyMandatoryFields"
          />
          <v-tooltip
            activator="parent"
            location="left"
          >
            {{ mandatoryFilter ? 'show all fields' : 'show only mandatory fields' }}
          </v-tooltip>
        </div>
        <WfmActionsList
          :actions="actionsToPerform?.length>0?actionsToPerform:null"
          :data-test-id="'form_' + folder.name"
          @action-click="performActionModal"
        />
        <div
          v-if="showBusinessesFilter"
          class="ms-3"
        >
          <WfmBusinessFilter :folder="folder" />
        </div>
      </div>
    </template>
    <template #right-utility>
      <div class="d-flex">
        <!-- refBys in toolbar -->
        <div
          v-if="showRef"
          class="justify-end row my-auto"
        >
          <WfmRefBy
            :ref-bys="refBy"
            :folder="folder"
            :row-data="rowData"
            @add-refby-tab="addRefByTab"
          />
        </div>

        <!-- update Button in Toolbar -->
        <div
          v-if="!readOnly && !isEditNotAllowed && !invoiceApprovalCase"
          class=""
        >
          <WfmButton
            button-class="update-btn"
            @button-click="handleClick()"
          >
            {{ txnToUse?.label?.eng||"null" }}
          </WfmButton>
        </div>
      </div>
    </template>
  </WfmToolbar>
  <WfmForm
    :key="formKey"
    class="propsheet-container"
    :class="customClass"
    :data-test-id="'wfm_form_' + folder.name"
  >
    <template #form-header>
      <div class="footer-content d-flex justify-content-between ">
        <!-- mandatory fields or invalid check  -->
        <div class="d-flex">
          <div
            v-if="missingFields.length!==0"
            class="fs-6 mt-1"
          >
            <span class="me-2">Mandatory*</span>
          </div>
          <div class="row">
            <div
              v-for="(item,index) in missingFields"
              :key="index"
              class="mt-2 col"
            >
              <span
                class="badge bg-danger mt-1"
                :style="{'cursor':'pointer' ,
                         'font-size':'12px'}"
                @click="scrollToTheField(item)"
              >{{ item.split('/')[1] }}</span>
            </div>
          </div>
        </div>
      </div>
    </template>
    <!-- body section of the form  -->
    <template #form-body>
      <WfmFieldGroup
        :params="rowData"
        :fields-to-display="fieldsToDisplay"
        :form-object-found="formObjectFound"
        :base-folder="folder"
        :current-task="currentTask"
        :filled-data="filledData"
        :ag-grid-api="agGridApi"
        :get-txn-to-run="getTxnToRun"
        :refresh-tab-data="refreshTabData"
      />
    </template>
    <template #form-footer>
      <div
        v-if="showLeaveQuota"
        class="leaves-info container"
      >
        <span class="mt-1 p-2">
          Leaves Left For {{ currentEmpName }}
        </span>
        <div class="row mt-3 text-center">
          <div
            class="mt-1"
            data-test-id="leaves_left_count"
          >
            Current month : {{ leavesQuota?.leaves_left }}
          </div>
          <div
            class="mt-1 text-center"
            data-test-id="yearly_leaves_left_count"
          >
            Current year : {{ leavesQuota?.yearly_leaves_left || 0 }}
          </div>
        </div>
      </div>
      <div
        v-if="showArrearReview"
        class="arrear-review"
        data-test-id="arrear_runs/review"
      >
        <div class="arrear-review-header">
          <div>
            <h6>Arrear Calculation Review</h6>
            <div class="arrear-review-subtitle">
              {{ arrearReviewSubtitle }}
            </div>
          </div>
          <button
            class="arrear-icon-button"
            type="button"
            data-test-id="arrear_runs/review/refresh"
            @click="loadArrearReview"
          >
            <v-icon
              name="bi-arrow-repeat"
              scale="1.1"
            />
            <v-tooltip
              activator="parent"
              location="bottom"
            >
              Refresh review
            </v-tooltip>
          </button>
        </div>
        <div
          v-if="arrearReview.loading"
          class="arrear-review-state"
        >
          Loading review...
        </div>
        <div
          v-else-if="arrearReview.error"
          class="arrear-review-state arrear-review-error"
        >
          {{ arrearReview.error }}
        </div>
        <template v-else>
          <div class="arrear-review-metrics">
            <div
              v-for="metric in arrearReviewMetrics"
              :key="metric.label"
              class="arrear-review-metric"
            >
              <span>{{ metric.label }}</span>
              <strong>{{ metric.value }}</strong>
            </div>
          </div>
          <div
            v-if="arrearReview.employees.length === 0"
            class="arrear-review-state"
          >
            No calculated arrears
          </div>
          <div
            v-else
            class="arrear-review-layout"
          >
            <div class="arrear-review-table-wrap">
              <table class="arrear-review-table">
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Earned Period</th>
                    <th>Payable Days</th>
                    <th>Gross</th>
                    <th>Deduction</th>
                    <th>Net</th>
                    <th>Invoice Billable</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="employee in arrearReview.employees"
                    :key="employee.arrear_payslips_id"
                    :class="{ selected: selectedArrearEmployee?.arrear_payslips_id === employee.arrear_payslips_id }"
                    @click="selectArrearEmployee(employee)"
                  >
                    <td>
                      <div class="arrear-review-employee-code">
                        {{ employee.employee_code }}
                      </div>
                      <div>{{ employee.employee_name || '-' }}</div>
                    </td>
                    <td>{{ formatRange(employee.arrear_pay_period) }}</td>
                    <td>{{ formatNumber(employee.payable_days, 2) }}</td>
                    <td>{{ formatMoney(employee.gross_arrear_amount) }}</td>
                    <td>{{ formatMoney(employee.deduction_arrear_amount) }}</td>
                    <td>{{ formatMoney(employee.net_arrear_amount) }}</td>
                    <td>{{ formatMoney(employee.invoice_billable_amount) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div
              v-if="selectedArrearEmployee"
              class="arrear-review-detail"
            >
              <div class="arrear-review-detail-header">
                <div>
                  <h6>
                    {{ selectedArrearEmployee.employee_name || '-' }}
                    ({{ selectedArrearEmployee.employee_code || '-' }}) - Payhead Details
                  </h6>
                  <div class="arrear-review-period">
                    <strong>Applicable Month:</strong>
                    {{ formatArrearMonth(selectedArrearEmployee.arrear_pay_period) || '-' }}
                    <span class="arrear-review-period-separator">|</span>
                    <strong>Arrear Period:</strong>
                    {{ formatRange(selectedArrearEmployee.arrear_pay_period) || '-' }}
                  </div>
                </div>
                <button
                  v-if="canViewArrearPayslip"
                  class="arrear-view-payslip-button"
                  type="button"
                  data-test-id="arrear_runs/review/viewPayslip"
                  @click="openArrearPayslip(selectedArrearEmployee)"
                >
                  <v-icon
                    name="bi-receipt"
                    scale="1"
                  />
                  <span>View Payslip</span>
                </button>
              </div>
              <div class="arrear-review-table-wrap">
                <table class="arrear-review-table arrear-review-detail-table">
                  <thead>
                    <tr>
                      <th>Payhead</th>
                      <th>Type</th>
                      <th>Old</th>
                      <th>Revised</th>
                      <th>Delta</th>
                      <th>Payable</th>
                      <th>Invoice</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="detail in selectedArrearEmployee.details"
                      :key="detail.id"
                    >
                      <td>{{ detail.payhead_name || formatCodeLabel(detail.payhead_code) }}</td>
                      <td>{{ detail.payhead_type_name || formatCodeLabel(detail.payhead_type_code) }}</td>
                      <td>{{ formatMoney(detail.old_amount) }}</td>
                      <td>{{ formatMoney(detail.revised_amount) }}</td>
                      <td>{{ formatMoney(detail.delta_amount) }}</td>
                      <td>{{ formatMoney(detail.payable_amount) }}</td>
                      <td>{{ formatMoney(detail.invoice_billable_amount) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </template>
      </div>
    </template>
    <!-- footer section modal -->
  </WfmForm>
  <WfmModal
    v-if="salaryStructureArrearImpactDialog.visible"
    custom-modal-div-class="salary-arrear-impact-modal"
    @close="cancelSalaryStructureArrearImpact"
  >
    <template #header>
      <div class="mx-auto my-auto">
        <h6>Salary Revision Arrear Impact</h6>
      </div>
    </template>
    <template #body>
      <div
        class="salary-arrear-impact-body"
        data-test-id="salary_structures/arrearImpactDialog"
      >
        <div class="salary-arrear-impact-summary">
          This salary update affects generated payroll.
        </div>
        <div class="salary-arrear-impact-grid">
          <div class="salary-arrear-impact-label">
            Payslips
          </div>
          <div>{{ salaryStructureArrearImpactDialog.payslipCount }}</div>
          <div class="salary-arrear-impact-label">
            Employees
          </div>
          <div>{{ salaryStructureArrearImpactDialog.employeeCount }}</div>
          <div class="salary-arrear-impact-label">
            Earned Period
          </div>
          <div>
            {{ salaryStructureArrearImpactDialog.arrearStart }}
            to
            {{ salaryStructureArrearImpactDialog.arrearEnd }}
          </div>
          <div class="salary-arrear-impact-label">
            Payroll Period
          </div>
          <div>
            {{ salaryStructureArrearImpactDialog.payrollStart }}
            to
            {{ salaryStructureArrearImpactDialog.payrollEnd }}
          </div>
        </div>
      </div>
    </template>
    <template #footer>
      <div class="d-flex justify-space-between w-100">
        <WfmButton
          button-class="ms-1 me-2"
          data-test-id="salary_structures/arrearImpactCancel"
          @button-click="cancelSalaryStructureArrearImpact"
        >
          Cancel
        </WfmButton>
        <WfmButton
          button-class="ms-1 save-btn me-2"
          data-test-id="salary_structures/arrearImpactConfirm"
          @button-click="confirmSalaryStructureArrearImpact"
        >
          Create Draft Arrear Run
        </WfmButton>
      </div>
    </template>
  </WfmModal>
</template>

<script>
import { computed, provide, ref, onMounted, onActivated, watch} from 'vue';
import {keys} from '../../provideKeys.js'
import WfmForm from '../../common/wfm-form.vue'
import WfmButton from '../../common/wfm-button.vue';
import WfmFieldGroup from '../wfm-folders/wfm-fieldGroup.vue'
import actionsListInFolder from '../../composables/actionsListInFolder'
import { toast } from 'vue3-toastify'
import foldersUtility from '../../composables/foldersUtility';
import makeFilter from '../../composables/makeFilter'
import postData from '../../composables/postData'
import getFatRow from '../../composables/fatRowGenerator'
import makeTxnForBulk from '../../composables/makeTxnForBulk'
import { sendMessage } from '../../services/websocket'
import makeTxn from '../../composables/makeTxn'
import processFormData from '../../composables/processFormData'
import processOtherForm from '../../composables/processOtherForm'
import getFormInfo from '../../composables/getFormInfo'
import usePartnersId from '../../composables/usePartnersId.js';
import WfmToolbar from '../../common/wfm-toolbar.vue';
import WfmActionsList from '../../common/wfm-actions-list.vue';
import useIcons from '../../composables/getIcons'
import WfmBusinessFilter from '../../common/wfm-business-filter.vue';
import permissionsCheck from '../../composables/permissionCheck'
import WfmRefBy from '../../common/wfm-refBy.vue';
import WfmModal from '../../common/wfm-modal.vue';
import getProjections from '../../composables/getProjections.js';
import getFolders from '../../composables/getFolders.js';

// composables

import { useStore } from 'vuex'

export default {
  name: 'FolderForm',
  components: {
    WfmForm,
    WfmFieldGroup,
    WfmButton,
    WfmToolbar,
    WfmActionsList,
    WfmRefBy,
    WfmBusinessFilter,
    WfmModal
  },
  props: {
    rowData: {
      type: Object,
      default : () => {}
    },
    fieldsToDisplay: {
      type: Array,
      default: () => []
    },
    formObjectFound: {
      type: Boolean,
      default : true
    },
    folder: {
      type: Object,
      default: null
    },
    refBy: {
      type: Object,
      default :null
    },
    currentTask:{
      type:String,
      default:null
    },
    txnToUse:{
      type:Object,
      default:null
    },
    rowNode:{
      type:Object,
      default:null
    },
    readOnly:{
      type:Boolean,
      default:false
    },
    agGridApi:{
      type:Object,
      default:null
    },
    customClass:{
      type:String,
      default:null
    },
    filledData:{
      type:Object,
      default:null
    }
  },
  emits: ['add-tab', 'change-form-state', 'del-tab', 'refresh-data', 'updateKeepAliveState'],
  setup(props, context) {
    //variables
    const store = useStore()
    const formData = ref({})
    const loadRefBy = ref(false)
    const {getIcon} = useIcons()
    provide(keys.formData, formData)
    const notificationId = ref()
    const listIcon = ref('bi-list')
    const listExpanded = ref(false)
    provide(keys.listExpanded, listExpanded)
    const mandatoryFilter = ref(false)
    //this flag is provided from the folderForm to tell the child component
    //it should be visible in the form only if its a mandatory field
    provide(keys.mandatoryFilter, mandatoryFilter);
    const mainFolderId = ref(props.rowData?.id)
    const incompleteForm = ref(false)
    const inValidFieldsPresent = ref(false)
    const formSaveClicked = ref(false)
    const sessionId = store.getters['sessionIdGetter'];
    const forceDelete = ref(false)
    const deleteTxn = ref(false)
    const deleteConfirmation = ref(false)
    const mandatoryOnlyIconColor = ref('white')
    const formKey = ref(6)
    const salaryStructureArrearImpactDialog = ref({
      visible: false,
      payslipCount: 0,
      employeeCount: 0,
      arrearStart: '',
      arrearEnd: '',
      payrollStart: '',
      payrollEnd: ''
    })
    const arrearReview = ref({
      loading: false,
      loaded: false,
      error: '',
      swhandle: null,
      header: null,
      employees: []
    })
    const selectedArrearPayslipId = ref(null)
    let salaryStructureArrearImpactResolve = null
    //const calendarData = ref()
    const leavesQuota = ref(null)
    const clearFormState = ref(false)
    provide(keys.clearFormState, clearFormState)
    provide(keys.leavesQuota, leavesQuota)
    const currentRowNode = ref(props.rowNode)
    const showLeaveQuota = computed(() => {
      return props.folder.name === 'leaves' && currentEmpId.value ? true : false
    })
    //composables declare
    const { getFormObject, getCurrentTxn } = getFormInfo()
    const {getCurrentSelectedPartnersId} = usePartnersId()
    const mandatoryFields = ref([])
    const invalidFields = ref([])
    provide(keys.mandatoryFields, mandatoryFields)
    provide(keys.invalidFields, invalidFields)
    const { getGroupedFormData, emptyTheContainer } = processFormData()
    const { getOtherFormDataGroup, emptyOthersContainer } = processOtherForm()
    const { getAllFoldersList, getCurrentFolder } = getFolders()

    onActivated(() => {
      //this is the best way so far. storing the rowData in vuex onactivated,
      //this then fixes issues like expr while switching tabs and folder etc.
      store.dispatch('commonModule/currentRowDataSetter', props.rowData);

      touchFormKey()
    })

    const {evalPermission} = permissionsCheck()

    // Refs
    // const {getNullObject} = getUtility()
    const showRef = computed(() => {
      if (props.currentTask === 'create') {
        return false
      }
      else {
        return true
      }
    })
    const folderFields = computed(() => {
      // console.log('rpwD', props)
      // if (props.currentTask === 'create') {
      //   const cloneOfFolderFields = JSON.parse(JSON.stringify(props.folder.fields));
      //   const emptyFolderFields = getNullObject(cloneOfFolderFields)
      //   return emptyFolderFields
      // }
      // else if (props.currentTask === 'update') {
      //   return props.rowData
      // }
      // else {
      //   return props.folder.fields
      // }
      return props.rowData
    })

    const showBusinessesFilter = computed(() => {
      const hasPartnersId = props.folder.fields.hasOwnProperty('partners_id')
      const excludedFolder = ['bulk_upload', 'salary_structures'].includes(props.folder.name)
      return hasPartnersId && !excludedFolder
    })
    provide(keys.folderFields, folderFields)
    const missingFields = computed(() => {
      let retVal = []
      if (formSaveClicked.value) {
        // const mandatoryFieldsInForm = getMandatoryFields()
        const mandatoryFieldsInForm = mandatoryFields.value
        if (props.currentTask === 'create') {
          retVal = mandatoryFieldsInForm.filter((field) => !formData.value.hasOwnProperty(field.split('/')[1]));
        }
      }
      return retVal
    })
    provide(keys.missingFields, missingFields)

    const bSettings = computed(() => {
      const bSettingString = store.getters['bSettings']
      return bSettingString
    })
    const resolvedSwhandle = computed(() => {
      return props.folder?.swhandle
        || props.txnToUse?.swhandle
        || bSettings.value?.env?.swhandle
        || store.getters['userModule/swHandle']
    })
    const showArrearReview = computed(() => {
      return props.folder?.name === 'arrear_runs' && props.currentTask !== 'create' && props.rowData?.id != null
    })
    const selectedArrearEmployee = computed(() => {
      if (!Array.isArray(arrearReview.value.employees) || arrearReview.value.employees.length === 0) {
        return null
      }
      const selected = arrearReview.value.employees.find((employee) => employee.arrear_payslips_id === selectedArrearPayslipId.value)
      return selected || arrearReview.value.employees[0]
    })
    const canViewArrearPayslip = computed(() => {
      const currentSwhandle = resolvedSwhandle.value || bSettings.value?.swhandle || bSettings.value?.env?.swhandle
      const reviewSwhandle = arrearReview.value?.swhandle
      return reviewSwhandle == null || reviewSwhandle === currentSwhandle
    })
    const arrearReviewSubtitle = computed(() => {
      if (arrearReview.value.loading) {
        return 'Loading'
      }
      const header = arrearReview.value.header
      if (header == null) {
        return ''
      }
      const status = header.status_name || formatCodeLabel(header.status_code)
      const reason = header.reason_name || formatCodeLabel(header.reason_code)
      return [status, reason].filter((part) => part != null && part !== '').join(' / ')
    })
    const arrearReviewMetrics = computed(() => {
      const header = arrearReview.value.header || props.rowData || {}
      return [
        { label: 'Employees', value: formatNumber(header.impacted_employee_count ?? arrearReview.value.employees.length, 0) },
        { label: 'Gross', value: formatMoney(header.gross_arrear_amount) },
        { label: 'Deduction', value: formatMoney(header.deduction_arrear_amount) },
        { label: 'Employer', value: formatMoney(header.employer_contribution_arrear_amount) },
        { label: 'Net', value: formatMoney(header.net_arrear_amount) },
        { label: 'Invoice Billable', value: formatMoney(header.invoice_billable_amount) }
      ]
    })
    //computed properties
    const invoiceApprovalCase = ref(props.txnToUse.name == 'txn_invoices_update')
    const isEditNotAllowed = computed(() => {
      //here checking if txn to run is allowed or not
      //if the target_swhandle is same as logged in allow
      if (showBusinessesFilter.value == false) return false;
      if (bSettings.value.env?.user_selected_partner?.[0]?.code == bSettings.value.swhandle) {
        return false
      }
      else {
        //if not same check shared_permission
        const sharedPermissionExpr = props.txnToUse?.shared_permissions?.envPermission
        const editAllowed = evalPermission(sharedPermissionExpr, bSettings.value.env)
        return !editAllowed
      }

    })
    provide(keys.editNotAllowed, isEditNotAllowed)
    const actionsToShow = computed(() => {
      const listOfTxn = props.folder.txns
      const {actionsToShowInForm} = actionsListInFolder(listOfTxn)
      const retVal = actionsToShowInForm()
      return retVal
    })
    const actions = computed(() => {
      const retVal = {name:'Actions', actions:[]}
      for (var i = 0; i < actionsToShow.value.length; i++) {
        const index = i
        const iconsName = actionsToShow?.value?.[i]?.label?.eng.split(' ')[0];
        const obj = {
          name:actionsToShow?.value?.[i]?.label?.eng,
          icon:getIcon(iconsName)?.name,
          fill:getIcon(iconsName)?.fill,
          action:() => {
            runTxn(actionsToShow.value[index])
          }
        }
        retVal.actions.push(obj)
      }
      return retVal
    })
    const actionsToPerform = computed(() => {
      const retVal = []
      if (props.currentTask !== 'create') {
        if (actions.value.actions.length > 0) {
          retVal.push(actions.value)
        }
      }
      return retVal
    })

    const currentEmpId = computed(() => {
      let retVal = null
      if (props.currentTask == 'create') {
        retVal = formData.value?.employees_id?.value?.[0]?.id
      }
      else if (props.currentTask == 'update') {
        retVal = props.rowData?.employees_id?.[0].id
      }
      return retVal
    })
    const currentEmpName = computed(() => {
      return formData.value?.employees_id?.value?.[0].persons_id?.[0].first_name?.eng || props.rowData?.employees_id?.[0].persons_id?.[0].first_name?.eng
    })
    const formObject = computed(() => {
      //get the current txn object depending on the txntype
      //get the form object that is needed
      let currentTxn = null
      if (props.txnToUse != null) {
        currentTxn = props.txnToUse
      }
      else {
        currentTxn = getCurrentTxn(
          props.currentTask,
          props.folder
        )
      }
      const formName = currentTxn?.form?.form_name ? currentTxn?.form?.form_name : currentTxn?.form
      return getFormObject(formName, props.folder)
    })
    // //watch for the change in the state of the formOpen and assign accordingly
    // watch(currentEmpId, (newValue) => {
    //   try {
    //     if (props.folder.name === 'leaves') {
    //       getLeavesQuota(currentEmpId.value)
    //     }
    //   } catch (error) {
    //     console.log(error)
    //   }
    // });

    //methods

    async function addRefByTab(tabsSpecs) {
      context.emit('add-tab', tabsSpecs)
    }

    function refByDataTestId(refs) {
      return `${props.folder.name}/refBy/${refs.label}`
    }
    function touchFormKey() {
      formKey.value = Math.floor(Math.random() * 1000000)
    }
    function scrollToTheField(fieldName) {
      var targetDiv = document.getElementById(fieldName);

      if (targetDiv) {
        // Scroll to the div
        targetDiv.scrollIntoView({ behavior: 'smooth', block: 'center' });
        targetDiv.focus()
      } else {
        console.error("Div with keyword '" + fieldName + "' not found.");
      }
    }
    function applyBusinessFilter(newValue) {
      // fieldsToFilter.value = newValue
    }

    function dependencyDelete() {
      forceDelete.value = true
      removeRecord()
    }

    const arrearActionTxns = [
      'txn_arrear_runs_calculate',
      'txn_arrear_runs_recalculate',
      'txn_arrear_runs_approve',
      'txn_arrear_runs_reject',
      'txn_arrear_runs_cancel',
      'txn_arrear_runs_reverse'
    ]

    function isArrearRunAction(txn) {
      return props.folder.name === 'arrear_runs' && arrearActionTxns.includes(txn?.name)
    }

    function arrearActionMessage(txn, response) {
      const rawRecord = response?.output?.data?.records?.[0] ?? response?.output?.data
      let data = rawRecord
      if (typeof rawRecord === 'string') {
        try {
          data = JSON.parse(rawRecord)
        }
        catch {
          data = null
        }
      }
      if (typeof data?.data === 'string') {
        try {
          data = JSON.parse(data.data)
        }
        catch {
          data = null
        }
      }
      const results = Array.isArray(data?.results) ? data.results : []
      if (txn?.name === 'txn_arrear_runs_calculate' || txn?.name === 'txn_arrear_runs_recalculate') {
        const payslipCount = results.reduce((sum, row) => sum + Number(row?.calculated_payslips || 0), 0)
        const employeeCount = results.reduce((sum, row) => sum + Number(row?.impacted_employee_count || 0), 0)
        return `Calculated ${payslipCount} arrear payslip(s) for ${employeeCount} employee(s)`
      }
      return 'done'
    }

    function arrearActionErrorMessage(error) {
      return error?.output?.message || error?.message || error?.data?.output?.message || 'Unable to process arrear run action'
    }

    async function refreshCurrentArrearRun() {
      if (!props.rowData?.id) {
        return null
      }
      const { projectionForFolder } = getProjections()
      const { listData } = postData()
      const txnParams = {
        refRows: true,
        filters: {
          joinop: 'or',
          filterInput: [
            { path: `${props.folder.name}.id`, operator: '=', value: props.rowData.id }
          ]
        }
      }
      const projections = projectionForFolder(props.folder.name)
      if (projections != null) {
        txnParams.projections = projections
      }
      const response = await listData(props.folder, txnParams)
      if (response?.output?.type !== 'success' || !Array.isArray(response?.output?.data?.records) || response.output.data.records.length === 0) {
        return null
      }
      response.output.data.records = response.output.data.records.map((record) => {
        if (typeof record === 'string') {
          return JSON.parse(record)
        }
        return record
      })
      const { fatRowGenerator } = getFatRow()
      const rows = fatRowGenerator(response)
      return rows?.[0] || null
    }

    async function executeArrearRunAction(txn) {
      if (!props.rowData?.id) {
        toast.warning('please select an arrear run')
        return
      }
      notificationId.value = toast.loading('working on it...', {
        position: toast.POSITION.TOP_CENTER
      })
      try {
        const txnToRun = JSON.parse(JSON.stringify(txn))
        const selectedTargetSwhandle = bSettings.value.env?.user_selected_partner?.[0]?.code
        txnToRun.swhandle = txnToRun.swhandle || resolvedSwhandle.value
        txnToRun.folder = txnToRun.folder || props.folder.name
        txnToRun.foldertype = txnToRun.foldertype || props.folder.foldertype || 'folders'
        txnToRun.session_key = sessionId
        txnToRun.target_swhandle = selectedTargetSwhandle || txnToRun.target_swhandle || txnToRun.swhandle
        txnToRun.params = {
          arrear_runs_id: [props.rowData.id]
        }
        const response = await sendMessage(txnToRun)
        if (response?.output?.type === 'error') {
          throw response
        }
        toast.update(notificationId.value, {
          render: arrearActionMessage(txnToRun, response),
          autoClose: 2500,
          closeButton: true,
          type: 'success',
          isLoading: false
        })
        try {
          const refreshedRow = await refreshCurrentArrearRun()
          if (refreshedRow != null) {
            currentRowNode.value?.[0]?.setData(refreshedRow)
            context.emit('refresh-data', refreshedRow)
            touchFormKey()
          }
          await loadArrearReview()
        }
        catch {
          // Keep the successful action toast even if row refresh fails.
        }
      }
      catch (error) {
        toast.update(notificationId.value, {
          render: `Error !${arrearActionErrorMessage(error)}`,
          closeButton: true,
          type: 'error',
          autoClose: 8000,
          isLoading: false
        })
      }
    }

    async function runTxn(txn) {
      if (isArrearRunAction(txn)) {
        await executeArrearRunAction(txn)
        return
      }
      //todo last and first check
      const txnNameArray = txn.name.split('_')
      if (txnNameArray[txnNameArray.length - 1] === 'delete') {
        removeRecord(txn)
      }
      else {
        const isTerminateTxn = txnNameArray[txnNameArray.length - 1]?.toLowerCase() === 'terminate'
        if (isTerminateTxn) {
          const leavingDate = formData.value?.leaving_date?.value ?? props.rowData?.leaving_date ?? null
          if (leavingDate == null || leavingDate === '') {
            toast.error('Leaving Date is mandatory to terminate employee')
            return
          }
          await makeParamsToSend(txn, 'Left')
        } else {
          const txnName = txnNameArray[txnNameArray.length - 1]
          await makeParamsToSend(txn, txnName)
        }

      }
    }
    function cancelDelete() {
      deleteConfirmation.value = false
    }
    async function makeParamsToSend(txn, bulkTxnName) {
      try {
        let selectedRows = props.agGridApi.getSelectedRows()
        currentRowNode.value = props.agGridApi.getSelectedNodes()
        if (selectedRows == null || selectedRows.length === 0) {
          selectedRows = props.rowData ? [props.rowData] : []
        }
        //start a notification of task progress  with specific id
        notificationId.value = toast.loading(`working to ${bulkTxnName}...`, {
          position: toast.POSITION.TOP_CENTER
        })
        const dataToChange = {path:'', value:[]}
        let docpSelected = [];
        const txnName = bulkTxnName
        let propertyName = null
        let docpSource = null
        const {changeWorkingFolder} = foldersUtility()
        let docpSourceFolder = null

        if (props.folder.name === 'employees') {
          propertyName = 'employee_status'
          docpSource = props.folder.fields[propertyName].source
          docpSourceFolder = changeWorkingFolder(docpSource, bSettings.value?.output.data.records[0])
        }
        else if (props.folder.name === 'leaves') {
          propertyName = 'leave_status'
          docpSource = props.folder.fields[propertyName]?.source;
          docpSourceFolder = changeWorkingFolder(docpSource, bSettings.value?.output.data.records[0])
        }
        //getting the docpId
        let txnParams = { refRows: true };
        if (props.folder.name === 'employees' && txnName.toLowerCase() === 'left') {
          txnParams.filters = {
            joinop: 'or',
            filterInput: [
              { path: `${docpSourceFolder.name}.code`, operator: '=', value: 'left' },
              { path: `${docpSourceFolder.name}.code`, operator: '=', value: 'terminated' },
            ],
          };
        } else {
          const { generateFilterParams } = makeFilter(txnName, docpSourceFolder, null, null, true);
          const filterParams = generateFilterParams();
          txnParams = {filters:filterParams, refRows:true};
          //adding the partners filter to get the target_swhandles data
          txnParams.filters.filterInput.push({joinop:'or', filterInput:[{ path:`${docpSourceFolder.name}.partners_id`, operator:'=', value:getCurrentSelectedPartnersId()}]})
        }

        const { listData } = postData()
        const list = await listData(docpSourceFolder, txnParams);
        if (list.output.type === 'error') {
          console.log(list.output.message);
          toast.update(notificationId.value, {
            render: list.output.message,
            type: 'error',
            isLoading: false,
            autoClose:3000,
            closeButton: true,
            position: toast.POSITION.TOP_CENTER,
          });
          return;
        }
        else {
          if (list.output.data.records.length > 0) {
            const { fatRowGenerator } = getFatRow();
            docpSelected = fatRowGenerator(list);
          } else {
            toast.update(notificationId.value, {
              render: `Employee status '${txnName}' not found`,
              type: 'error',
              isLoading: false,
              autoClose:3000,
              closeButton: true,
              position: toast.POSITION.TOP_CENTER,
            });
            return;
          }
        }
        //loop thru each selected row to create formData
        for (var i = 0; i < selectedRows?.length; i++) {
          if (props.folder.name === 'leaves') {
            dataToChange.path = 'leaves.leave_status'
            if (selectedRows[i].leave_status?.[0].id) {
              dataToChange.value.push({oldDocpId:selectedRows[i].leave_status?.[0].id, newId:docpSelected[0]?.id, txnOp:'U', mainFolderId:selectedRows[i].id, propertyName:propertyName, params:selectedRows[i]})
            }
            else {
              dataToChange.value.push({oldDocpId:null, newId:docpSelected[0]?.id, txnOp:'C', mainFolderId:selectedRows[i].id, propertyName:propertyName, params:selectedRows[i]})

            }
          }

          else if (props.folder.name === 'employees') {
            dataToChange.path = 'employees.employee_status'
            const leavingDate = formData.value?.leaving_date?.value ?? props.rowData?.leaving_date ?? null
            const commonPayload = {
              newId:docpSelected[0]?.id,
              txnOp:'U',
              mainFolderId:selectedRows[i].id,
              propertyName:propertyName,
              params:selectedRows[i],
              leaving_date: leavingDate
            }
            if (selectedRows[i][propertyName] && selectedRows[i][propertyName].length != 0) {
              dataToChange.value.push({oldDocpId:selectedRows[i][propertyName][0].id, ...commonPayload})
            }
            else {
              dataToChange.value.push({oldDocpId:null, ...commonPayload, txnOp:'C'})

            }
          }

        }

        const bulkFormData = dataToChange
        //generate the txnToRun with txn and fieldsData
        const { generateTxn } = makeTxnForBulk(
          bulkFormData,
          props.folder,
          txn,
          txnName
        )

        const txnToRun = generateTxn()
        txnToRun.session_key = sessionId;
        txnToRun.target_swhandle = bSettings.value.env?.user_selected_partner?.[0]?.code
        const response = await sendMessage(txnToRun)
        await handleResult(response)
      } catch (err) {
        toast.update(notificationId.value, {
          render: err?.message || 'Unable to process action',
          type: 'error',
          isLoading: false,
          autoClose:3000,
          closeButton: true,
          position: toast.POSITION.TOP_CENTER,
        });
      }
    }

    async function removeRecord(txn) {
      deleteTxn.value = true
      //as we are dealing with tabs selected row should be the one which is the active tab
      // gridApi.value.deselectAll();
      // currentRowNode.value[0]?.setSelected(true)
      const currentRecord = props.rowData
      const txnType = 'delete'
      const dataToDelete = []
      dataToDelete.push({id: currentRecord.id})
      notificationId.value = toast.loading('working on it...', {
        position: toast.POSITION.TOP_CENTER
      })

      const params = {
        normalFieldList:dataToDelete,
        folderDetails:props.folder,
        currentTaskName:props.currentTask,
        txnType:txnType,
        currentFolderId:mainFolderId.value
      }

      //generate the txnToRun with txn and fieldsData
      const { generateTxn } = makeTxn(params)
      const txnToRun = generateTxn()
      txnToRun.session_key = sessionId;
      if (forceDelete.value) {
        txnToRun.deleteDependentData = true
      }
      const list = await sendMessage(txnToRun)
      await handleResult(list)
      forceDelete.value = false
      txnToRun.deleteDependentData = false

    }
    //handle the retVal
    async function handleResult(data) {

      if (data.output.type === 'success') {
        const { fatRowGenerator } = getFatRow()
        //add the new row to the top without refreshing whole grid
        if (props.currentTask === 'create') {
          if (Object.keys(data.output.data.records).length !== 0) {
            const records = [];
            for (let i = 0; i < data.output.data.records.length; i++) {
              records.push(JSON.parse(data.output.data.records[i]))
            }
            data.output.data.records = records;
            const listData = fatRowGenerator(data)
            props.agGridApi?.applyTransaction({
              add: [listData[0]],
              addIndex: 0
            })
          }
          // currentTaskName.value = "";
          //empty the formData
        } else {
          //make the grid update without refresh
          const records = [];
          for (let i = 0; i < data.output.data.records.length; i++) {
            records.push(JSON.parse(data.output.data.records[i]))
          }
          data.output.data.records = records;

          const listData = fatRowGenerator(data)
          if (listData[0] && !deleteTxn.value) {
            //general update txn should handle here
            if (currentRowNode.value !== undefined) {
              currentRowNode.value?.[0]?.setData(listData[0])
            }
            context.emit('refresh-data', listData[0])

          } else {
            //in case of delete txn match the rowID from the list and remove that row
            const selectedRowId = props.rowData.id
            const completeRows = []
            props.agGridApi.forEachNode((node) => completeRows.push(node.data));
            const rowsToRemove = completeRows.filter((row) => selectedRowId == row.id);
            props.agGridApi?.applyTransaction({
              remove: rowsToRemove
            })
            //close the tab
            context.emit('del-tab')
          }

        }
        //if success show update the notification with success status
        toast.update(notificationId.value, {
          render: 'done',
          autoClose: 1000,
          closeButton: true,
          type: 'success',
          isLoading: false
        })
        clearState()
        touchFormKey()
      }
      if (data.output.type === 'error') {
        if (!deleteTxn.value) {
          context.emit('change-form-state', true)
        }
        if (deleteTxn.value) {
          deleteConfirmation.value = true
        }
        toast.update(notificationId.value, {
          render: data.output.message,
          type: 'error',
          isLoading: false,
          autoClose:3000,
          closeButton: true,
          position: toast.POSITION.TOP_CENTER,
        });
      }
      //empty the arrays that is returned to make the txn after its used to avoid being used again in another txn
      emptyTheContainer()
      emptyOthersContainer()
    }

    function performActionModal(action) {
      console.log('handle action in the modal')
      action()
    }

    function clearState() {
      try {
        //empty the form data
        formSaveClicked.value = false
        listIcon.value = 'bi-list'
        // store.dispatch("commonModule/formDataSetter", {})
        formData.value = {}
        if (props.currentTask === 'create') {
          clearFormState.value = true;
        }
        //empty the gridApis
        store.dispatch('agGridModule/detailGridApiSetter', [])
        listExpanded.value = false
        // mandatoryCheck("reset")
        //invalidCheck("reset")
        context.emit('change-form-state', false)

      } catch (error) {
        console.log(error)
      }
    }
    //expand the list
    function expandTheList() {
      listExpanded.value = !listExpanded.value
      if (listExpanded.value) {
        listIcon.value = 'bi-list-nested'
      } else {
        listIcon.value = 'bi-list'
      }
    }

    function showOnlyMandatoryFields() {
      //here the mandatoryFilter will change which is provided in each controls
      mandatoryFilter.value = !mandatoryFilter.value

      mandatoryOnlyIconColor.value = mandatoryFilter.value ? 'red' : 'white'
    }

    function refreshTabData(rowData) {
      context.emit('refresh-data', rowData);
    }

    function parseTxnPayload(response) {
      const rawRecord = response?.output?.data?.records?.[0] ?? response?.output?.data
      let payload = rawRecord
      if (typeof payload === 'string') {
        try {
          payload = JSON.parse(payload)
        }
        catch {
          return null
        }
      }
      if (typeof payload?.data === 'string') {
        try {
          payload = JSON.parse(payload.data)
        }
        catch {
          return null
        }
      }
      return payload
    }

    function selectArrearEmployee(employee) {
      selectedArrearPayslipId.value = employee?.arrear_payslips_id || null
    }

    function normalizeArrearReviewPayload(payload) {
      const employees = Array.isArray(payload?.employees) ? payload.employees : []
      return {
        loading: false,
        loaded: true,
        error: '',
        swhandle: payload?.swhandle || payload?.target_swhandle || null,
        header: payload?.header || null,
        employees
      }
    }

    async function loadArrearReview() {
      if (!showArrearReview.value) {
        return
      }
      const txn = getTxnByName('txn_arrear_runs_review')
      if (txn == null) {
        arrearReview.value = {
          loading: false,
          loaded: true,
          error: 'Arrear review transaction is not available',
          swhandle: null,
          header: null,
          employees: []
        }
        return
      }
      arrearReview.value = {
        ...arrearReview.value,
        loading: true,
        error: ''
      }
      try {
        const selectedTargetSwhandle = bSettings.value.env?.user_selected_partner?.[0]?.code
        const txnToRun = JSON.parse(JSON.stringify(txn))
        txnToRun.swhandle = txnToRun.swhandle || resolvedSwhandle.value
        txnToRun.folder = txnToRun.folder || props.folder.name
        txnToRun.foldertype = txnToRun.foldertype || props.folder.foldertype || 'folders'
        txnToRun.session_key = sessionId
        txnToRun.target_swhandle = selectedTargetSwhandle || txnToRun.target_swhandle || txnToRun.swhandle
        txnToRun.params = {
          arrear_runs_id: [props.rowData.id]
        }
        const response = await sendMessage(txnToRun)
        if (response?.output?.type === 'error') {
          throw response
        }
        const payload = parseTxnPayload(response)
        arrearReview.value = normalizeArrearReviewPayload(payload)
        if (arrearReview.value.employees.length > 0) {
          const stillSelected = arrearReview.value.employees.some((employee) => employee.arrear_payslips_id === selectedArrearPayslipId.value)
          if (!stillSelected) {
            selectedArrearPayslipId.value = arrearReview.value.employees[0].arrear_payslips_id
          }
        }
        else {
          selectedArrearPayslipId.value = null
        }
      }
      catch (error) {
        arrearReview.value = {
          loading: false,
          loaded: true,
          error: arrearActionErrorMessage(error),
          swhandle: null,
          header: null,
          employees: []
        }
      }
    }

    function formatNumber(value, fractionDigits = 0) {
      const parsed = Number(value || 0)
      if (!Number.isFinite(parsed)) {
        return ''
      }
      return parsed.toLocaleString('en-IN', {
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits
      })
    }

    function formatMoney(value) {
      return formatNumber(value, 2)
    }

    function formatCodeLabel(value) {
      if (value == null) {
        return ''
      }
      return String(value)
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (letter) => letter.toUpperCase())
    }

    function parseRangeBounds(value) {
      if (value == null) {
        return []
      }
      const text = String(value)
      const quotedMatches = [...text.matchAll(/"([^"]+)"/g)].map((match) => match[1])
      if (quotedMatches.length >= 2) {
        return quotedMatches.slice(0, 2)
      }
      const dateMatches = [...text.matchAll(/\d{4}-\d{2}-\d{2}(?:[ T]\d{2}:\d{2}:\d{2}(?:\.\d+)?)?/g)].map((match) => match[0])
      return dateMatches.slice(0, 2)
    }

    function formatRange(value) {
      const bounds = parseRangeBounds(value)
      if (bounds.length < 2) {
        return value || ''
      }
      return `${formatImpactDate(bounds[0])} - ${formatExclusiveRangeEndDate(bounds[1])}`
    }

    function formatArrearMonth(value) {
      const bounds = parseRangeBounds(value)
      const date = toBusinessDate(bounds[0] || value)
      if (Number.isNaN(date.getTime())) {
        return ''
      }
      const month = date.toLocaleString('default', { month: 'long', timeZone: 'UTC' })
      return `${month} ${date.getUTCFullYear()}`
    }

    async function openArrearPayslip(employee) {
      if (employee?.uconst == null && employee?.arrear_payslips_id == null) {
        return
      }
      try {
        const folderList = getAllFoldersList(bSettings.value)
        const payslipsFolder = getCurrentFolder('payslips', folderList)
        if (payslipsFolder == null) {
          toast.error('Payslips folder is not available')
          return
        }
        const employeeName = employee.employee_name || employee.employee_code || 'Arrear Payslip'
        const payrollMonth = parseRangeBounds(employee.arrear_pay_period)?.[0] || parseRangeBounds(employee.pay_period)?.[0]
        const monthDate = toBusinessDate(payrollMonth)
        const monthOfSlip = Number.isNaN(monthDate.getTime()) ? '' : monthDate.toLocaleString('default', { month: 'short' })
        const yearOfSlip = Number.isNaN(monthDate.getTime()) ? '' : monthDate.getFullYear()
        if (employee.uconst != null) {
          context.emit('add-tab', {
            label: employeeName,
            component: 'HtmlTemplate',
            name: `${employeeName} ${monthOfSlip}/${yearOfSlip} Arrear`,
            params: {
              readOnly: true,
              folder: payslipsFolder,
              data: {
                id: employee.arrear_payslips_id,
                uconst: employee.uconst,
                pay_period: employee.pay_period,
                arrear_pay_period: employee.arrear_pay_period,
                payroll_month: payrollMonth
              }
            }
          })
          return
        }
        const { projectionForFolder } = getProjections()
        const { listData } = postData()
        const response = await listData(payslipsFolder, {
          refRows: true,
          filters: {
            joinop: 'or',
            filterInput: [
              { path: 'payslips.id', operator: '=', value: employee.arrear_payslips_id }
            ]
          },
          projections: projectionForFolder('payslips')
        })
        if (response?.output?.type === 'error') {
          throw response
        }
        response.output.data.records = response.output.data.records.map((record) => {
          if (typeof record === 'string') {
            return JSON.parse(record)
          }
          return record
        })
        const { fatRowGenerator } = getFatRow()
        const rows = fatRowGenerator(response)
        const payslip = rows?.[0]
        if (payslip == null) {
          toast.error('Arrear payslip not found')
          return
        }
        context.emit('add-tab', {
          label: employeeName,
          component: 'HtmlTemplate',
          name: `${employeeName} ${monthOfSlip}/${yearOfSlip} Arrear`,
          params: {
            readOnly: true,
            folder: payslipsFolder,
            data: payslip
          }
        })
      }
      catch (error) {
        toast.error(arrearActionErrorMessage(error))
      }
    }

    function getTxnByName(name) {
      const txns = props.folder?.txns
      if (Array.isArray(txns)) {
        return txns.find((txn) => txn?.name === name)
      }
      return txns?.[name] || Object.values(txns || {}).find((txn) => txn?.name === name)
    }

    function getPatchRowsByPath(patch, path) {
      const rows = []
      if (!Array.isArray(patch)) {
        return rows
      }
      for (let i = 0; i < patch.length; i++) {
        if (patch[i]?._path === path) {
          for (let j = i + 1; j < patch.length; j++) {
            if (patch[j]?._path != null || patch[j]?.pathIndexes != null) {
              break
            }
            rows.push(patch[j])
          }
        }
      }
      return rows
    }

    function parseTxnJsonRecord(response) {
      const record = response?.output?.data?.records?.[0]
      if (record == null) {
        return null
      }
      if (typeof record === 'string') {
        try {
          return JSON.parse(record)
        } catch (err) {
          return null
        }
      }
      return record
    }

    const BUSINESS_TIMEZONE_OFFSET_MINUTES = 330

    function parseStoredTimestamp(value) {
      if (value == null) {
        return null
      }
      if (value instanceof Date) {
        return value
      }
      const text = String(value)
      if (/[zZ]$|[+-]\d{2}:?\d{2}$/.test(text)) {
        return new Date(text)
      }
      return new Date(`${text.replace(' ', 'T')}Z`)
    }

    function toBusinessDate(value, subtractMs = 0) {
      const parsed = parseStoredTimestamp(value)
      if (parsed == null) {
        return new Date(NaN)
      }
      return new Date(parsed.getTime() - subtractMs + BUSINESS_TIMEZONE_OFFSET_MINUTES * 60 * 1000)
    }

    function formatImpactDate(value) {
      if (value == null) {
        return ''
      }
      const date = toBusinessDate(value)
      if (Number.isNaN(date.getTime())) {
        return String(value)
      }
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      return `${String(date.getUTCDate()).padStart(2, '0')} ${months[date.getUTCMonth()]} ${date.getUTCFullYear()}`
    }

    function formatExclusiveRangeEndDate(value) {
      if (value == null) {
        return ''
      }
      const date = toBusinessDate(value, 1)
      if (Number.isNaN(date.getTime())) {
        return String(value)
      }
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      return `${String(date.getUTCDate()).padStart(2, '0')} ${months[date.getUTCMonth()]} ${date.getUTCFullYear()}`
    }

    function buildTsRange(start, end) {
      if (start == null || end == null) {
        return null
      }
      const startDate = new Date(start)
      const endDate = new Date(end)
      if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
        return null
      }
      return `["${startDate.toISOString()}", "${endDate.toISOString()}")`
    }

    function addOnePayrollMonth(value) {
      const utcDate = new Date(value)
      if (Number.isNaN(utcDate.getTime())) {
        return null
      }
      const offsetMs = BUSINESS_TIMEZONE_OFFSET_MINUTES * 60 * 1000
      const businessDate = new Date(utcDate.getTime() + offsetMs)
      const rawTargetMonth = businessDate.getUTCMonth() + 1
      const targetYear = businessDate.getUTCFullYear() + Math.floor(rawTargetMonth / 12)
      const targetMonth = rawTargetMonth % 12
      const targetLastDay = new Date(Date.UTC(targetYear, targetMonth + 1, 0)).getUTCDate()
      const targetDay = Math.min(businessDate.getUTCDate(), targetLastDay)
      const targetBusinessTime = Date.UTC(
        targetYear,
        targetMonth,
        targetDay,
        businessDate.getUTCHours(),
        businessDate.getUTCMinutes(),
        businessDate.getUTCSeconds(),
        businessDate.getUTCMilliseconds()
      )
      return new Date(targetBusinessTime - offsetMs).toISOString()
    }

    function applyDefaultSalaryStructureArrearFields(salaryPatch, impact) {
      const arrearPeriod = buildTsRange(impact?.min_pay_period, impact?.max_pay_period)
      const payrollStart = impact?.max_pay_period
      const payrollEnd = addOnePayrollMonth(payrollStart)
      const payrollPeriod = buildTsRange(payrollStart, payrollEnd)
      if (arrearPeriod == null || payrollPeriod == null) {
        return null
      }
      salaryPatch.create_arrear_run = true
      salaryPatch.arrear_period = arrearPeriod
      salaryPatch.arrear_payroll_period = payrollPeriod
      salaryPatch.arrear_invoice_period = payrollPeriod
      if (salaryPatch.revision_remarks == null || String(salaryPatch.revision_remarks).trim() === '') {
        salaryPatch.revision_remarks = `Auto-created from backdated salary update. Arrear earned period ${formatImpactDate(impact.min_pay_period)} to ${formatExclusiveRangeEndDate(impact.max_pay_period)}.`
      }
      return { arrearPeriod, payrollPeriod, payrollStart, payrollEnd }
    }

    function closeSalaryStructureArrearImpactDialog(result) {
      salaryStructureArrearImpactDialog.value.visible = false
      if (salaryStructureArrearImpactResolve != null) {
        salaryStructureArrearImpactResolve(result)
        salaryStructureArrearImpactResolve = null
      }
    }

    function confirmSalaryStructureArrearImpact() {
      closeSalaryStructureArrearImpactDialog(true)
    }

    function cancelSalaryStructureArrearImpact() {
      closeSalaryStructureArrearImpactDialog(false)
    }

    function openSalaryStructureArrearImpactDialog(impact, defaults) {
      salaryStructureArrearImpactDialog.value = {
        visible: true,
        payslipCount: impact.payslip_count || 0,
        employeeCount: impact.employee_count || 0,
        arrearStart: formatImpactDate(impact.min_pay_period),
        arrearEnd: formatExclusiveRangeEndDate(impact.max_pay_period),
        payrollStart: formatImpactDate(defaults.payrollStart),
        payrollEnd: formatExclusiveRangeEndDate(defaults.payrollEnd)
      }
      return new Promise((resolve) => {
        salaryStructureArrearImpactResolve = resolve
      })
    }

    async function resolveSalaryStructureUpdateTxn(txnToRun) {
      if (
        props.folder?.name !== 'salary_structures' ||
        props.currentTask !== 'update' ||
        txnToRun?.name !== 'txn_salary_structures_update'
      ) {
        return txnToRun
      }

      const impactTxnMeta = getTxnByName('txn_salary_structures_arrear_impact_check')
      if (impactTxnMeta == null) {
        return txnToRun
      }

      const impactTxn = JSON.parse(JSON.stringify(impactTxnMeta))
      impactTxn.params = txnToRun.params
      impactTxn.session_key = sessionId
      impactTxn.target_swhandle = txnToRun.target_swhandle
      const impactResponse = await sendMessage(impactTxn)
      if (impactResponse?.output?.type === 'error') {
        toast.update(notificationId.value, {
          render: impactResponse.output.message,
          type: 'error',
          isLoading: false,
          autoClose: 6000,
          closeButton: true,
          position: toast.POSITION.TOP_CENTER,
        })
        return null
      }

      const impact = parseTxnJsonRecord(impactResponse)
      if (impact?.has_impact !== true) {
        return txnToRun
      }

      const salaryPatch = getPatchRowsByPath(txnToRun.params, 'business.folders.salary_structures')[0]
      if (salaryPatch == null) {
        return txnToRun
      }
      const defaults = applyDefaultSalaryStructureArrearFields(salaryPatch, impact)
      if (defaults == null) {
        toast.update(notificationId.value, {
          render: 'Backdated salary change impacts generated payroll, but the arrear/payroll periods could not be derived. Use Revise Salary Structure to enter arrear details manually.',
          type: 'error',
          isLoading: false,
          autoClose: 9000,
          closeButton: true,
          position: toast.POSITION.TOP_CENTER,
        })
        return null
      }

      toast.update(notificationId.value, {
        render: 'Review salary revision arrear impact',
        type: 'info',
        isLoading: false,
        autoClose: 1500,
        closeButton: true,
        position: toast.POSITION.TOP_CENTER,
      })
      const confirmed = await openSalaryStructureArrearImpactDialog(impact, defaults)
      if (!confirmed) {
        toast.update(notificationId.value, {
          render: 'cancelled',
          type: 'info',
          isLoading: false,
          autoClose: 2000,
          closeButton: true,
          position: toast.POSITION.TOP_CENTER,
        })
        return null
      }
      notificationId.value = toast.loading('working on it...', {
        position: toast.POSITION.TOP_CENTER,
      })

      const reviseTxnMeta = getTxnByName('txn_salary_structures_revise')
      const reviseTxn = JSON.parse(JSON.stringify(reviseTxnMeta || txnToRun))
      reviseTxn.name = 'txn_salary_structures_revise'
      reviseTxn.params = txnToRun.params
      reviseTxn.session_key = sessionId
      reviseTxn.target_swhandle = txnToRun.target_swhandle
      return reviseTxn
    }

    function getTxnToRun() {
      try {
        const sessionId = store.getters['sessionIdGetter'];
        const {
          normalFieldList,
          docpList,
          detailList,
          detailKeyFieldName,
        } = getGroupedFormData(formData.value, props.folder, null)
        const params = {
          normalFieldList:normalFieldList,
          docpList:docpList,
          detailList:detailList,
          folderDetails:props.folder,
          currentTaskName:props.currentTask,
          detailKeyName:detailKeyFieldName,
          currentFolderId:mainFolderId.value,
          txn:props.txnToUse
        }

        //generate the txnToRun with txn and fieldsData
        const { generateTxn } = makeTxn(params)
        const txnToRun = generateTxn();
        txnToRun.session_key = sessionId;
        txnToRun.target_swhandle = bSettings.value.env?.user_selected_partner?.[0]?.code
        return txnToRun;
      }
      catch (err) {
        console.log(err);
      }
    }

    //on clicking save run the transaction
    async function saveHandle(data, txnType) {
      try {
        console.log('test test save handle')
        formSaveClicked.value = true
        //check if mandatory fields are filled if not stop txn
        const dataToSave = data ? data : formData.value
        listIcon.value = 'bi-list'
        if (missingFields?.value.length > 0) {
          incompleteForm.value = true
        }
        else if (invalidFields.value.length > 0) {
          inValidFieldsPresent.value = true
        }
        else {
          incompleteForm.value = false
          inValidFieldsPresent.value = false
        }
        //if mandatoryfields not missing
        if (incompleteForm.value && props.currentTask === 'create') {
          toast.error('please check all the fields')
        }
        else if (inValidFieldsPresent.value) {
          toast.error('please correct the invalid inputs in fields')
        }
        else {
          // formData.mainFolderId = mainFolderId.value
          //if formObject is found
          //get the form object that is needed
          //close the modal
          notificationId.value = toast.loading('working on it...', {
            position: toast.POSITION.TOP_CENTER,
          })
          // formOpen.value = false
          if (formObject.value !== undefined) {
            let txnToRun = getTxnToRun();
            txnToRun = await resolveSalaryStructureUpdateTxn(txnToRun)
            if (txnToRun == null) {
              emptyTheContainer()
              emptyOthersContainer()
              return
            }
            const list = await sendMessage(txnToRun)
            context.emit('change-form-state', false)
            await handleResult(list)
          }
          //if no formObject found for the currentTxn then make useful groups differently
          else {
            const { fieldsData, docpList, detailList, detailKeyFieldName } =
              getOtherFormDataGroup(dataToSave, props.folder, txnType)
            //generate the txnToRun with txn and fieldsData
            const params = {
              normalFieldList:fieldsData,
              docpList:docpList,
              detailList:detailList,
              folderDetails:props.folder,
              currentTaskName:props.currentTask,
              detailKeyName:detailKeyFieldName,
              txnType:txnType,
              currentFolderId:mainFolderId.value,
            }
            const { generateTxn } = makeTxn(params)
            const txnToRun = generateTxn()
            //if txn to run is not found (some folders dont have update) show error
            if (!txnToRun || txnToRun === undefined) {
              toast.error('sorry folder cant be updated by you')
            } else {
              const  list = await sendMessage(txnToRun)
              await handleResult(list)
            }
          }
        }
      } catch (err) {
        throw err
      }
    }

    function handleClick(name) {
      if (!isEditNotAllowed.value) {
        saveHandle()
      }
    }
    //shortCut keys


    watch(() => props.rowData?.id, () => {
      selectedArrearPayslipId.value = null
      loadArrearReview()
    })

    onMounted(() => {
      //on mount get the leavesQuota to show on form if employees id is found
      // if (showLeaveQuota.value && currentEmpId.value) {
      //   getLeavesQuota(currentEmpId.value)
      // }
      loadArrearReview()
    })
    return {
      addRefByTab,
      listIcon,
      showRef,
      //isUser,
      refByDataTestId,
      handleClick,
      actionsToPerform,
      performActionModal,
      expandTheList,
      showOnlyMandatoryFields,
      mandatoryOnlyIconColor,
      listExpanded,
      missingFields,
      scrollToTheField,
      formKey,
      deleteConfirmation,
      salaryStructureArrearImpactDialog,
      confirmSalaryStructureArrearImpact,
      cancelSalaryStructureArrearImpact,
      showArrearReview,
      arrearReview,
      arrearReviewSubtitle,
      arrearReviewMetrics,
      selectedArrearEmployee,
      canViewArrearPayslip,
      loadArrearReview,
      selectArrearEmployee,
      openArrearPayslip,
      formatNumber,
      formatMoney,
      formatCodeLabel,
      formatRange,
      formatArrearMonth,
      dependencyDelete,
      cancelDelete,
      showLeaveQuota,
      mandatoryFilter,
      loadRefBy,
      leavesQuota,
      currentEmpName,
      isEditNotAllowed,
      invoiceApprovalCase,
      applyBusinessFilter,
      showBusinessesFilter,
      getTxnToRun,
      refreshTabData
      //selectRefBy
    };

  }};
</script>
<style>
.dropdown-container-class{
   text-transform: capitalize;
   padding:3px;
   background-color: var(--refBy-background);
   color:var(--refBy-color) !important;
   border-radius: 6px;
   font-size:12px;
   padding-left:8px;
   font-weight: 600;
   box-shadow: rgba(50, 50, 93, 0.25) 0px 2px 5px -1px, rgba(0, 0, 0, 0.3) 0px 1px 3px -1px;
   padding-right:8px;
}
</style>

<style scoped>
.toolbar-class{
  background-color: var(--toolbar-color);
  padding-top:30px;
  padding-bottom:30px;
  margin-top: 0px;

}

.update-btn {
     color: #6faa5dff;
     color: white;
     border: 1px solid white;
     height: fit-content;
     font-weight: 600;
     font-size: 12px;
     margin:10px 20px;
     border-radius: 8px;
    box-shadow: 1px 1px #2e688dff
}

.update-btn:hover {
     color: white;
     border: 1px solid white;
     background-color: var(--co-main-color);
 }
 .propsheet-container{
width: 100%;
padding:20px;
height: 78vh;
margin-bottom: 10px;
padding-bottom:10px;
overflow-y: scroll;
background:var(--main-background-color);
border-bottom-right-radius: 8px;
border-bottom-left-radius: 8px;
}

.footer-content{
  background-color: var(--main-background-color);
  padding:10px;
}

.leaves-info{
  margin-top:50px;
  color:var(--co-main-color);
  font-weight: 600;
  padding:300px;
}

:deep(.salary-arrear-impact-modal) {
  width: min(560px, calc(100vw - 32px));
}

.salary-arrear-impact-body {
  padding: 12px 18px 18px;
  min-width: 320px;
}

.salary-arrear-impact-summary {
  margin-bottom: 14px;
  font-weight: 600;
}

.salary-arrear-impact-grid {
  display: grid;
  grid-template-columns: 130px minmax(0, 1fr);
  gap: 10px 16px;
  align-items: start;
}

.salary-arrear-impact-label {
  font-weight: 600;
}

.arrear-review {
  margin-top: 28px;
  padding-top: 18px;
  border-top: 1px solid #d6e2ea;
  color: #1f2f3a;
}

.arrear-review-header,
.arrear-review-detail-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.arrear-review-header h6,
.arrear-review-detail-header h6 {
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  overflow-wrap: anywhere;
}

.arrear-review-subtitle {
  margin-top: 4px;
  color: #5d6f7a;
  font-size: 12px;
}

.arrear-review-period-separator {
  margin: 0 8px;
  color: #90a0aa;
}

.arrear-icon-button,
.arrear-view-payslip-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 30px;
  border: 1px solid #2e688d;
  background: #ffffff;
  color: #2e688d;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 9px;
  border-radius: 6px;
}

.arrear-icon-button {
  width: 32px;
  padding: 4px;
}

.arrear-icon-button:hover,
.arrear-view-payslip-button:hover {
  background: #eaf4f9;
}

.arrear-review-state {
  padding: 12px 0;
  color: #5d6f7a;
  font-size: 13px;
}

.arrear-review-error {
  color: #b00020;
}

.arrear-review-metrics {
  display: grid;
  grid-template-columns: repeat(6, minmax(120px, 1fr));
  gap: 8px;
  margin-bottom: 14px;
}

.arrear-review-metric {
  min-width: 0;
  padding: 9px 10px;
  border: 1px solid #d6e2ea;
  background: #ffffff;
  border-radius: 6px;
}

.arrear-review-metric span {
  display: block;
  margin-bottom: 2px;
  color: #5d6f7a;
  font-size: 11px;
}

.arrear-review-metric strong {
  display: block;
  overflow-wrap: anywhere;
  font-size: 14px;
}

.arrear-review-layout {
  display: grid;
  grid-template-columns: minmax(460px, 1.1fr) minmax(360px, 0.9fr);
  gap: 16px;
  align-items: start;
}

.arrear-review-table-wrap {
  width: 100%;
  overflow-x: auto;
  border: 1px solid #d6e2ea;
  border-radius: 6px;
  background: #ffffff;
}

.arrear-review-table {
  width: 100%;
  min-width: 760px;
  border-collapse: collapse;
  font-size: 12px;
}

.arrear-review-detail-table {
  min-width: 700px;
  table-layout: fixed;
  font-size: 12px;
  line-height: 1.35;
}

.arrear-review-table th,
.arrear-review-table td {
  padding: 8px 10px;
  border-bottom: 1px solid #e6edf2;
  text-align: left;
  vertical-align: top;
}

.arrear-review-table th {
  color: #2c4658;
  background: #eef6fa;
  font-weight: 700;
  white-space: nowrap;
}

.arrear-review-table tbody tr {
  cursor: pointer;
}

.arrear-review-table tbody tr:hover,
.arrear-review-table tbody tr.selected {
  background: #f2f8fb;
}

.arrear-review-table tbody tr:last-child td {
  border-bottom: 0;
}

.arrear-review-employee-code {
  color: #2e688d;
  font-weight: 700;
}

.arrear-review-detail {
  min-width: 0;
  height: min(560px, calc(100vh - 280px));
  min-height: 420px;
  display: flex;
  flex-direction: column;
}

.arrear-review-detail > .arrear-review-table-wrap {
  flex: 1;
  min-height: 0;
  overflow: auto;
}

.arrear-review-detail-table th,
.arrear-review-detail-table td {
  overflow-wrap: anywhere;
}

@media (max-width: 520px) {
  .salary-arrear-impact-grid {
    grid-template-columns: 1fr;
    gap: 4px;
  }
}

@media (max-width: 1100px) {
  .arrear-review-metrics {
    grid-template-columns: repeat(3, minmax(120px, 1fr));
  }

  .arrear-review-layout {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 640px) {
  .arrear-review-metrics {
    grid-template-columns: repeat(2, minmax(120px, 1fr));
  }

  .arrear-review-header,
  .arrear-review-detail-header {
    align-items: flex-start;
  }
}

</style>
